import api from './api';
import router from '../../router';

const pathRegex = /^\/(planning|userGroups)/;

const state = {
	error: null,
	userGroups: [],
	selectedGroup: null,
};

const getters = {
	getGroupsByUserId: (state) => (userId) => {
		return state.userGroups.filter((group) =>
			group.users.some((user) => {
				if (typeof user === 'string') {
					return user === userId;
				} else {
					return user._id === userId;
				}
			})
		);
	},
	selectedGroup: (state) => state.selectedGroup,
};

const mutations = {
	setError(state, { error }) {
		state.error = error;
	},

	setUserGroups(state, userGroups) {
		if (state.userGroups.length > 0) {
			return;
		}
		state.userGroups = userGroups;
	},

	setDeleted(state, { groupId, deleted }) {
		state.userGroups = state.userGroups.map((group) => {
			if (group._id === groupId) {
				return { ...group, deleted };
			}
			return group;
		});
	},

	addUserGroup(state, userGroup) {
		const exists = state.userGroups.some((g) => g._id === userGroup._id);
		if (!exists) {
			state.userGroups.push(userGroup);
		}
	},
	updateUserGroup(state, userGroup) {
		const index = state.userGroups.findIndex((g) => g.id === userGroup.id);
		if (index === -1) return;

		const updated = { ...state.userGroups[index], ...userGroup };
		state.userGroups[index] = updated;
		if (state.selectedGroup && state.selectedGroup._id === userGroup.id) {
			state.selectedGroup = { ...state.selectedGroup, ...userGroup };
		}
	},
	setSelectedGroup(state, group) {
		state.selectedGroup = group;
	},
};

const actions = {
	selectGroup({ commit, state }, group) {
		commit('setSelectedGroup', group);
	},
	async load({ commit }) {
		try {
			if (state.userGroups.length > 0) {
				return;
			}
			const userGroups = (await api.list()).data;
			commit('setUserGroups', userGroups);
		} catch (err) {
			commit('setError', { error: err.response.data.message });
		}
	},
	async loadActiveUserGroups({ commit }) {
		const deleted = false;
		try {
			const userGroups = (await api.list(deleted)).data;
			commit('setUserGroups', userGroups);
		} catch (err) {
			commit('setError', { error: err.response.data.message });
		}
	},
	async create({ commit }, userGroup) {
		try {
			const result = await api.create(userGroup.group, userGroup.userIDs);

			if (result.status === 201) {
				commit('addUserGroup', result.data);
				commit('setSelectedGroup', result.data);
			}
			return result;
		} catch (err) {
			commit('setError', { error: err.response.data.message });
			throw err;
		}
	},
	async update({ commit }, userGroup) {
		try {
			const result = await api.update(userGroup.group, userGroup.userIDs);
			const payload = result.data;
			payload.id = userGroup.group._id ? userGroup.group._id : userGroup.group.id;
			commit('updateUserGroup', payload);
			return result;
		} catch (err) {
			commit('setError', { error: err.response.data.message });
			throw err;
		}
	},
	async delete({ commit }, groupId) {
		try {
			await api.delete(groupId);
			commit('setDeleted', { groupId, deleted: true });
		} catch (err) {
			commit('setError', { error: err.response.data.message });
			throw err;
		}
	},
	async restore({ commit }, groupId) {
		try {
			const result = await api.update({ id: groupId, deleted: false });
			commit('setDeleted', { groupId, deleted: false });
		} catch (err) {
			commit('setError', { error: err.response.data.message });
			throw err;
		}
	},
	clearError({ commit }) {
		commit('setError', { error: null });
	},
	resolveUserGroupUsers({ rootState }, userGroup) {
		if (!Array.isArray(userGroup?.users)) {
			return userGroup;
		}

		userGroup.users = userGroup.users.map((userId) => {
			const userDoc = rootState.users.usersMap[userId];
			if (!userDoc) {
				return userId;
			}
			const { _id, username, firstName, lastName, pensum, displayName, sortName, isActive, id } = userDoc;
			return { _id, username, firstName, lastName, pensum, displayName, sortName, isActive, id };
		});

		return userGroup;
	},
	async socket_created_UserGroup({ dispatch, commit, rootState }, payload) {
		if (!pathRegex.test(router.currentRoute.value.path)) {
			return;
		}
		const userGroup = payload?.[0]?.payload;
		if (!userGroup) {
			return;
		}

		dispatch('resolveUserGroupUsers', userGroup);

		commit('addUserGroup', userGroup);
	},
	async socket_updated_UserGroup({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute.value.path)) {
			return;
		}

		const update = payload?.[0]?.payload;
		if (!update) {
			return;
		}

		dispatch('resolveUserGroupUsers', update);
		commit('updateUserGroup', update);
	},
	async socket_deleted_UserGroup({ dispatch, commit }, payload) {
		if (!pathRegex.test(router.currentRoute.value.path)) {
			return;
		}
		commit('setDeleted', { groupId: payload?.[0]?.payload?.id, deleted: true });
		await dispatch('load');
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
